import React, { useState, useEffect, useCallback } from 'react';
import { Package, Clock, BookOpen, Users, Wrench, Lock, CheckCircle2, Sparkles, ArrowRight, Lightbulb, Award, AlertCircle, Loader2 } from 'lucide-react';
import { useFamilyContext } from '../../../contexts/FamilyContext';

// Prompt definitions with unlock sequence
const PROMPTS = [
  {
    id: 'sentimental_objects',
    title: 'Sentimental Objects',
    icon: Package,
    description: '5 things you\'ve kept strictly for sentimental value (or that would be garbage to anyone but you)',
    coachVoice: 'Be specific. Not "a photo" but "the polaroid from junior prom where everyone\'s eyes are closed." Not "a gift" but "the terrible clay mug my sister made that leaks."',
    locked: false,
    order: 1
  },
  {
    id: 'five_years',
    title: 'Had 5+ Years',
    icon: Clock,
    description: '5 things you\'ve had longer than 5 years',
    coachVoice: 'What have you held onto through multiple life stages? Think about why you keep bringing these things with you.',
    locked: true,
    order: 2
  },
  {
    id: 'tell_story',
    title: 'Tell a Story',
    icon: BookOpen,
    description: '5 things in your room that tell a story',
    coachVoice: 'Each object has a narrative. What happened? Who was there? What changed because of it?',
    locked: true,
    order: 3
  },
  {
    id: 'tied_to_people',
    title: 'Tied to People',
    icon: Users,
    description: '5 things that remind you of specific people',
    coachVoice: 'Not just "from" someone, but things that make you think of them. What does the object reveal about your relationship?',
    locked: true,
    order: 4
  },
  {
    id: 'made_built',
    title: 'Made/Built',
    icon: Wrench,
    description: '5 things you made, built, or created yourself',
    coachVoice: 'Include failures and experiments. The broken prototype often tells a better story than the polished version.',
    locked: true,
    order: 5
  }
];

export default function SentimentalObjectsGenerator() {
  const { currentUser } = useFamilyContext();
  const [view, setView] = useState('prompt-select'); // 'prompt-select', 'object-list', 'object-details', 'results', 'library'
  const [availablePrompts, setAvailablePrompts] = useState(PROMPTS);
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [objects, setObjects] = useState(['', '', '', '', '']);
  const [currentObjectIndex, setCurrentObjectIndex] = useState(0);
  const [objectDetails, setObjectDetails] = useState({});
  const [objectOpenings, setObjectOpenings] = useState({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [completedSessions, setCompletedSessions] = useState([]);
  const [showUnlockAnimation, setShowUnlockAnimation] = useState(false);

  const loadCompletedSessions = useCallback(async () => {
    // TODO: Load from database
    // For now, check localStorage
    const saved = localStorage.getItem(`object_sessions_${currentUser?.id}`);
    if (saved) {
      const sessions = JSON.parse(saved);
      setCompletedSessions(sessions);
      
      // Unlock prompts based on completed sessions
      const updated = availablePrompts.map(prompt => {
        const completed = sessions.find(s => s.promptId === prompt.id);
        if (completed || prompt.order === 1) {
          return { ...prompt, locked: false };
        }
        // Unlock next prompt if previous is complete
        const previousPrompt = availablePrompts.find(p => p.order === prompt.order - 1);
        const previousComplete = sessions.find(s => s.promptId === previousPrompt?.id);
        return { ...prompt, locked: !previousComplete };
      });
      setAvailablePrompts(updated);
    }
  }, [currentUser?.id, availablePrompts]);

  // Load completed sessions on mount
  useEffect(() => {
    loadCompletedSessions();
  }, [loadCompletedSessions]);

  const startPrompt = (prompt) => {
    setCurrentPrompt(prompt);
    setObjects(['', '', '', '', '']);
    setObjectDetails({});
    setObjectOpenings({});
    setCurrentObjectIndex(0);
    setView('object-list');
  };

  const handleObjectListComplete = () => {
    const filledObjects = objects.filter(o => o.trim().length > 0);
    if (filledObjects.length < 5) {
      alert('Please list all 5 objects to continue.');
      return;
    }
    setCurrentObjectIndex(0);
    setView('object-details');
  };

  const handleObjectDetailComplete = (index, detail) => {
    setObjectDetails(prev => ({ ...prev, [index]: detail }));
    
    if (index < 4) {
      setCurrentObjectIndex(index + 1);
    } else {
      // All details collected, move to openings
      setCurrentObjectIndex(0);
      // Stay in same view, just shift focus
    }
  };

  const handleOpeningComplete = (index, opening) => {
    setObjectOpenings(prev => ({ ...prev, [index]: opening }));
    
    if (index < 4) {
      setCurrentObjectIndex(index + 1);
    } else {
      // All openings collected, analyze
      analyzeObjects();
    }
  };

  const analyzeObjects = async () => {
    setIsAnalyzing(true);
    setView('results');

    try {
      const response = await fetch('/api/essay-ideas/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          interviewType: 'generate_from_objects',
          studentName: currentUser?.name || currentUser?.displayName || 'this student',
          responses: objects.map((obj, i) => ({
            object: obj,
            why: objectDetails[i],
            opening: objectOpenings[i]
          })),
          context: {
            promptType: currentPrompt.id,
            promptTitle: currentPrompt.title
          },
          userId: currentUser?.id
        })
      });

      if (!response.ok) throw new Error('Analysis failed');

      const data = await response.json();

      // Transform response format from unified API to component format
      const transformedData = {
        rankedTopics: (data.essayTopics || []).map(topic => ({
          ...topic,
          opening: topic.hook || topic.opening, // Map 'hook' to 'opening'
          strengths: topic.specificStrengths || topic.strengths || [] // Map 'specificStrengths' to 'strengths'
        })),
        overallInsight: data.surpriseInsight || data.magneticHook
      };

      setAnalysis(transformedData);

      // Save completed session
      const session = {
        promptId: currentPrompt.id,
        promptTitle: currentPrompt.title,
        completedAt: new Date().toISOString(),
        topTopics: transformedData.rankedTopics.slice(0, 2).map(t => t.object || t.title)
      };

      const updated = [...completedSessions, session];
      setCompletedSessions(updated);
      localStorage.setItem(`object_sessions_${currentUser?.id}`, JSON.stringify(updated));

      // Unlock next prompt with animation
      unlockNextPrompt();

    } catch (error) {
      console.error('Analysis error:', error);
      alert('Failed to analyze objects. Please try again.');
    }

    setIsAnalyzing(false);
  };

  const unlockNextPrompt = () => {
    const currentOrder = currentPrompt.order;
    const nextPrompt = availablePrompts.find(p => p.order === currentOrder + 1);
    
    if (nextPrompt && nextPrompt.locked) {
      setShowUnlockAnimation(true);
      setTimeout(() => {
        setAvailablePrompts(prev => 
          prev.map(p => p.id === nextPrompt.id ? { ...p, locked: false } : p)
        );
        setShowUnlockAnimation(false);
      }, 2000);
    }
  };

  const renderPromptSelect = () => (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="bg-white border-2 border-slate-900 rounded-xl overflow-hidden">
        <div className="px-8 py-6 bg-slate-900 border-l-4 border-l-teal-400">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-2 h-2 rounded-full bg-teal-400"></div>
            <h2 className="text-xl font-bold text-white">Essay Idea Generator</h2>
          </div>
          <p className="text-sm text-stone-300 pl-4">Find your essay topics through objects that matter to you</p>
        </div>

        {completedSessions.length > 0 && (
          <div className="px-8 py-6 bg-white border-t-2 border-slate-900">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-slate-900">
                  {completedSessions.reduce((sum, s) => sum + (s.topTopics?.length || 2), 0)} essay topics found
                </p>
                <p className="text-xs text-slate-600">
                  across {completedSessions.length} prompt{completedSessions.length > 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => setView('library')}
                className="px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors"
              >
                View Library →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-5 gap-3">
        {availablePrompts.map((prompt) => {
          const isCompleted = completedSessions.some(s => s.promptId === prompt.id);
          const PromptIcon = prompt.icon;
          
          return (
            <div
              key={prompt.id}
              className={`
                text-center py-3 border-2 rounded-lg transition-all
                ${prompt.locked 
                  ? 'border-stone-300 bg-stone-50' 
                  : isCompleted
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-slate-900 bg-white'
                }
              `}
            >
              {prompt.locked ? (
                <Lock className="w-5 h-5 text-stone-400 mx-auto" />
              ) : isCompleted ? (
                <CheckCircle2 className="w-5 h-5 text-teal-600 mx-auto" />
              ) : (
                <PromptIcon className="w-5 h-5 text-slate-900 mx-auto" />
              )}
              <p className="text-xs font-medium text-slate-700 mt-2">{prompt.title}</p>
            </div>
          );
        })}
      </div>

      {/* Prompt Cards */}
      <div className="space-y-4">
        {availablePrompts.map((prompt, idx) => {
          const isCompleted = completedSessions.some(s => s.promptId === prompt.id);
          const PromptIcon = prompt.icon;
          
          return (
            <div
              key={prompt.id}
              className={`
                bg-white rounded-xl border-2 overflow-hidden transition-all
                ${prompt.locked 
                  ? 'border-stone-300 opacity-60' 
                  : isCompleted
                  ? 'border-teal-500 hover:shadow-lg cursor-pointer'
                  : 'border-slate-900 hover:shadow-lg cursor-pointer'
                }
              `}
              onClick={() => !prompt.locked && startPrompt(prompt)}
            >
              {/* Header Bar */}
              <div className={`px-6 py-3 flex items-center justify-between ${
                prompt.locked 
                  ? 'bg-stone-100' 
                  : isCompleted
                  ? 'bg-teal-500'
                  : 'bg-slate-900'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${
                    prompt.locked ? 'bg-stone-400' : 'bg-white'
                  }`}></div>
                  <span className={`text-xs font-bold uppercase tracking-wide ${
                    prompt.locked ? 'text-stone-600' : 'text-white'
                  }`}>
                    {prompt.title}
                  </span>
                  {!prompt.locked && !isCompleted && idx > 0 && (
                    <span className="text-xs font-bold uppercase tracking-wide text-emerald-900 bg-emerald-400 px-2 py-0.5 rounded">
                      New
                    </span>
                  )}
                </div>
                
                {prompt.locked ? (
                  <Lock className="w-4 h-4 text-stone-500" />
                ) : isCompleted ? (
                  <CheckCircle2 className="w-4 h-4 text-white" />
                ) : (
                  <PromptIcon className="w-4 h-4 text-white" />
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-sm text-slate-900 mb-4 leading-relaxed">{prompt.description}</p>
                
                {!prompt.locked && (
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-4">
                    <p className="text-xs text-slate-800 italic leading-relaxed">{prompt.coachVoice}</p>
                  </div>
                )}
                
                {prompt.locked ? (
                  <p className="text-xs text-slate-500 font-medium">
                    ✓ Complete previous prompt to unlock
                  </p>
                ) : isCompleted ? (
                  <div className="flex items-center gap-2 text-teal-700 font-medium text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Completed • View results in library</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-slate-900 font-bold text-sm">
                    <span>Start this prompt</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showUnlockAnimation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border-2 border-slate-900 p-8 max-w-md mx-4">
            <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-slate-900">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2 text-center">New Prompt Unlocked!</h3>
            <p className="text-sm text-slate-700 text-center">
              You've completed this session. A new prompt is now available to explore more angles.
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const renderObjectList = () => (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white border-2 border-slate-900 rounded-xl overflow-hidden">
        <div className="px-6 py-4 bg-slate-900 border-l-4 border-l-teal-400">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-teal-400"></div>
            <h3 className="text-sm font-bold text-white uppercase tracking-wide">{currentPrompt.title}</h3>
          </div>
        </div>
        <div className="p-6 bg-white">
          <p className="text-sm text-slate-900 mb-4 leading-relaxed">{currentPrompt.description}</p>
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <p className="text-xs text-slate-800 italic leading-relaxed">{currentPrompt.coachVoice}</p>
          </div>
        </div>
      </div>

      {/* Object Input Fields */}
      <div className="space-y-3">
        {objects.map((obj, idx) => (
          <div key={idx} className="bg-white rounded-lg border-2 border-slate-900 overflow-hidden">
            <div className="px-4 py-2 bg-slate-900">
              <label className="text-xs font-bold text-white uppercase tracking-wide">
                Object {idx + 1}
              </label>
            </div>
            <div className="p-4">
              <input
                type="text"
                value={obj}
                onChange={(e) => {
                  const updated = [...objects];
                  updated[idx] = e.target.value;
                  setObjects(updated);
                }}
                placeholder="e.g., The polaroid from junior prom where everyone's eyes are closed"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={() => setView('prompt-select')}
          className="px-6 py-3 border-2 border-slate-900 text-slate-900 rounded-lg font-bold hover:bg-slate-50 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={handleObjectListComplete}
          className="flex-1 bg-slate-900 text-white px-6 py-3 rounded-lg font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
        >
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const renderObjectDetails = () => {
    const allDetailsComplete = Object.keys(objectDetails).length === 5;
    const _allOpeningsComplete = Object.keys(objectOpenings).length === 5;
    const currentStage = !allDetailsComplete ? 'details' : 'openings';

    return (
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Progress Header */}
        <div className="bg-white border-2 border-slate-900 rounded-xl overflow-hidden">
          <div className="px-6 py-4 bg-slate-900 border-l-4 border-l-teal-400">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-teal-400"></div>
                <h3 className="text-sm font-bold text-white uppercase tracking-wide">
                  {currentStage === 'details' ? 'Why You Keep Them' : 'Opening Sentences'}
                </h3>
              </div>
              <span className="text-xs font-bold text-white">
                {currentObjectIndex + 1} of 5
              </span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div 
                className="bg-teal-400 h-2 rounded-full transition-all"
                style={{ width: `${((currentObjectIndex + 1) / 5) * 100}%` }}
              />
            </div>
          </div>

          {/* Current Object Display */}
          <div className="p-6 bg-white border-t-2 border-slate-900">
            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 mb-4">
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">
                Object {currentObjectIndex + 1}:
              </p>
              <p className="text-sm text-slate-900 font-medium">{objects[currentObjectIndex]}</p>
            </div>

            {currentStage === 'details' ? (
              <>
                <label className="block text-xs font-bold text-slate-900 uppercase tracking-wide mb-3">
                  Why do you still have this?
                </label>
                <textarea
                  value={objectDetails[currentObjectIndex] || ''}
                  onChange={(e) => {
                    setObjectDetails(prev => ({ ...prev, [currentObjectIndex]: e.target.value }));
                  }}
                  placeholder="2-3 sentences about why this object matters to you..."
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
                  rows={4}
                />
              </>
            ) : (
              <>
                <label className="block text-xs font-bold text-slate-900 uppercase tracking-wide mb-3">
                  Write the opening sentence (or two) of this essay
                </label>
                <textarea
                  value={objectOpenings[currentObjectIndex] || ''}
                  onChange={(e) => {
                    setObjectOpenings(prev => ({ ...prev, [currentObjectIndex]: e.target.value }));
                  }}
                  placeholder="Start your essay about this object..."
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
                  rows={3}
                />
                <p className="text-xs text-slate-500 mt-2 flex items-center gap-2">
                  <Sparkles className="w-3 h-3" />
                  Auto-saves as you type
                </p>
              </>
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-3">
          {currentObjectIndex > 0 && (
            <button
              onClick={() => setCurrentObjectIndex(currentObjectIndex - 1)}
              className="px-6 py-3 border-2 border-slate-900 text-slate-900 rounded-lg font-bold hover:bg-slate-50 transition-colors"
            >
              ← Previous
            </button>
          )}
          <button
            onClick={() => {
              if (currentStage === 'details') {
                handleObjectDetailComplete(currentObjectIndex, objectDetails[currentObjectIndex]);
              } else {
                handleOpeningComplete(currentObjectIndex, objectOpenings[currentObjectIndex]);
              }
            }}
            className="flex-1 bg-slate-900 text-white px-6 py-3 rounded-lg font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
          >
            {currentObjectIndex < 4 ? 'Next Object →' : currentStage === 'details' ? 'Continue to Openings →' : 'Analyze Topics →'}
          </button>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    if (isAnalyzing) {
      return (
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-xl border-2 border-slate-900 p-8 text-center">
            <Loader2 className="w-12 h-12 text-teal-500 animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Analyzing Your Objects</h3>
            <p className="text-sm text-slate-600">
              Evaluating energy, story potential, and differentiation for each topic...
            </p>
          </div>
        </div>
      );
    }

    if (!analysis) return null;

    return (
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white border-2 border-slate-900 rounded-xl overflow-hidden">
          <div className="px-8 py-5 bg-slate-900 border-l-4 border-l-teal-400">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-2 h-2 rounded-full bg-teal-400"></div>
              <h2 className="text-lg font-bold text-white">Your Essay Topics</h2>
            </div>
            <p className="text-sm text-stone-300 pl-4">
              Ranked by potential • {analysis.rankedTopics.length} topics analyzed
            </p>
          </div>
        </div>

        {/* Topic Cards */}
        {analysis.rankedTopics.map((topic, idx) => {
          const isTop = idx === 0;
          
          return (
            <div 
              key={idx}
              className={`bg-white rounded-xl border-2 overflow-hidden ${
                isTop ? 'border-gold-400' : 'border-slate-900'
              }`}
            >
              {/* Topic Header */}
              <div className={`px-6 py-3 flex items-center justify-between ${
                isTop ? 'bg-gold-400' : 'bg-slate-900'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${
                    isTop ? 'bg-slate-900' : 'bg-teal-400'
                  }`}></div>
                  <span className={`text-xs font-bold uppercase tracking-wide ${
                    isTop ? 'text-slate-900' : 'text-white'
                  }`}>
                    {isTop ? '🥇 TOP PICK' : `Topic ${idx + 1}`}
                  </span>
                </div>
                {isTop && (
                  <Award className="w-4 h-4 text-slate-900" />
                )}
              </div>

              {/* Topic Content */}
              <div className="p-8">
                <h3 className="text-base font-bold text-slate-900 mb-4">{topic.object}</h3>
                
                {/* Opening Quote */}
                <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 mb-6">
                  <p className="text-sm text-slate-800 italic leading-relaxed">"{topic.opening}"</p>
                </div>

                {/* Why This Works */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
                    <p className="text-xs font-bold text-slate-900 uppercase tracking-wide">Why this works:</p>
                  </div>
                  <ul className="space-y-2 pl-3">
                    {topic.strengths.map((strength, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <div className="w-1 h-1 rounded-full bg-slate-400 mt-2 flex-shrink-0"></div>
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Essay Angle */}
                <div className="bg-teal-50 border border-teal-300 rounded-lg p-4 mb-6">
                  <p className="text-xs font-bold text-slate-900 uppercase tracking-wide mb-2">Essay angle:</p>
                  <p className="text-sm text-slate-800 leading-relaxed">{topic.essayAngle}</p>
                </div>

                {/* Action Button */}
                <button className="w-full bg-slate-900 text-white px-6 py-3 rounded-lg font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2">
                  Develop This Essay <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}

        {/* Bottom Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => setView('prompt-select')}
            className="flex-1 bg-white border-2 border-slate-900 text-slate-900 px-6 py-3 rounded-lg font-bold hover:bg-slate-50 transition-colors"
          >
            Try Another Prompt
          </button>
          <button
            onClick={() => setView('library')}
            className="flex-1 bg-slate-900 text-white px-6 py-3 rounded-lg font-bold hover:bg-slate-800 transition-colors"
          >
            View All Topics
          </button>
        </div>
      </div>
    );
  };

  // Render based on current view
  return (
    <div className="p-6">
      {view === 'prompt-select' && renderPromptSelect()}
      {view === 'object-list' && renderObjectList()}
      {view === 'object-details' && renderObjectDetails()}
      {view === 'results' && renderResults()}
    </div>
  );
}
